import amplitude from 'amplitude-js';
import moment from 'moment';
import i18n from '../i18n';
import requestService from './requestService';
import { showSuccess, showError } from './toastService';
import { isInterpretationPrebookNeeded } from '../utils/luonaSpecificData';
import { createAttachmentError, getErrorStatus, isTrustedS3Url } from './attachmentUtils';

const endpoints = {
  getPrebookings: '/prebooking/',
  editPrebooking: 'prebooking/',
  acceptPrebooking: 'prebooking/',
  createPrebooking: '/prebooking/create',
  cancelPrebooking: '/prebooking/cancel/',
  getFreeSlots: 'prebooking/freeSlots',
  createMulti: 'prebooking/createMulti',
  getInterpreterCount: '/prebooking/interpreterCount',
  prebookingPrequery: 'chatroom/prebookingPrequery/v1',
  // Attachment endpoints (use 'platform' apiType, no /api/v1 prefix)
  attachmentPresignedUrl: 'prebooking/{prebookingId}/attachment/presigned-url',
  attachmentConfirm: 'prebooking/{prebookingId}/attachment/confirm',
  attachment: 'prebooking/{prebookingId}/attachment',
  attachmentDownload: 'prebooking/{prebookingId}/attachment/download',
};

const formatParametrs = (parametrsArray) => {
  return parametrsArray.filter(param => param).join('&');
};

export const getPrebookings = async(onlyPersonal = false, userType, interpreterUserId, dateFrom, dateTo) => {
  const onlyPersonalParam = onlyPersonal ? 'onlyPersonal=true' : '';
  const interpreterParam = interpreterUserId ? `targetUser=${interpreterUserId}` : '';
  const interpreterTypeParams = userType ? `userType=${userType}` : '';
  const dateFromParam = dateFrom ? `dateFrom=${moment(dateFrom).format('YYYY-MM-DD')}`: '';
  const dateToParam = dateTo ? `dateTo=${moment(dateTo).format('YYYY-MM-DD')}`: '';

  try {
    return requestService.get(`${endpoints.getPrebookings}`, formatParametrs([onlyPersonalParam, interpreterParam, interpreterTypeParams, dateFromParam, dateToParam, 'cancelled=true']), {apiType: 'platform-v2'});
  } catch (e) {
    console.error(e);
    if (e.response !== undefined && e.response.status === 400) {
      amplitude.getInstance().logEvent('Accept request failed', { 'HTTP status code': e.response ? String(e.response.status) : '' });
      showError(i18n('error500'));
    } else {
      amplitude.getInstance().logEvent('Accept request failed', { 'HTTP status code': e.response ? String(e.response.status) : '' });
      showError(i18n('error500'));
    }
    throw e;
  }
};

export const createPrebooking = async(prebook) => {
  const body = getPrebookBody(prebook);

  try {
    const prebookData = await requestService.postWithJson(
      `${endpoints.createPrebooking}`,
      null,
      body,
      { apiType: 'platform-v2'}
    );

    if (prebookData.error) {
      throw new Error(prebookData.message);
    }

    showSuccess(i18n('prebook_successfully_created'));
    return prebookData;
  } catch (e) {
    console.error(e);

    if (e.response !== undefined && e.response.status === 400) {
      amplitude.getInstance().logEvent('Failed to create prebooking', {
        error: e.message,
        'HTTP status code': e.response ? String(e.response.status) : null
      });
      showError(i18n('failed_to_create_prebooking'));
    } else {
      amplitude.getInstance().logEvent('Failed to create prebooking', {
        error: e.message,
        'HTTP status code': e.response ? String(e.response.status) : null
      });
      showError(i18n('failed_to_create_prebooking'));
    }

    throw e;
  }
};


export const getPrebookBody = (prebook) => {
  const data = {
    date: prebook.date ? prebook.date.getTime() : null,
    duration: prebook.duration ? prebook.duration * 60 :null,
    languageId: prebook.selectedLanguageId ? prebook.selectedLanguageId :null,
    targetLanguageId: prebook.selectedTargetLanguageId ? prebook.selectedTargetLanguageId :null,
    contractId: prebook.contractId ? prebook.contractId : null,
    skillIds: prebook.selectedSkillIds?.length > 0 ? prebook.selectedSkillIds?.join() : null,
    callType: prebook.callType ? prebook.callType : null,
    invoiceUserGroupId: prebook.invoiceUserGroupId ? prebook.invoiceUserGroupId :null,
    invoiceRef: prebook.invoiceRef ? prebook.invoiceRef :null,
    invoiceRecipientRef: prebook.invoiceRecipientRef ? prebook.invoiceRecipientRef :null,
    comments: prebook.comment ? prebook.comment :null,
    presence: prebook.presence ? prebook.presence :null,
    locationCity: prebook.locationCity ? prebook.locationCity :null,
    streetAddress: prebook.streetAddress ? prebook.streetAddress :null,
    postcode: prebook.postcode,
    subject: prebook.subject ? prebook.subject : null,
    externalOrder: prebook.externalOrderId ? prebook.externalOrderId : null,
    messageToInterpreter: prebook.messageToInterpreter ? prebook.messageToInterpreter : null,
    ccEmails: prebook.ccEmails ? prebook.ccEmails :null,
    patientName: prebook.patientName ? prebook.patientName : null,
    customerServiceComments: prebook.customerServiceComments || '',
    favoriteInterpreterIds: prebook.favoriteInterpreters?.length > 0 ? prebook.favoriteInterpreters?.join() :null,
    alternativeLanguageId: prebook.alternativeLanguageId || null,
    backupCallTypeForOnSite: prebook.backupCallTypeForOnSite || null,
    backupPresenceForOnSite: prebook.backupPresenceForOnSite || null,
    // 0 is a meaningful value here — it tells the backend to bypass the
    // contractual distance filter (TUL-4887 "Unlimited").
    distanceLimitOverrideMeters: typeof prebook.distanceLimitOverrideMeters === 'number'
      ? prebook.distanceLimitOverrideMeters
      : null,
    customFields: prebook?.customFields || null,
    allowedLevelJoins: prebook?.allowedLevelJoins || null,
    vatIncluded: prebook.vatIncluded,
  };
  return Object.keys(data).reduce((memo, field) => {
    if (data[field] || data[field] === false || data[field] === 0) {
      memo[field] = data[field];
    }
    return memo;
  }, {});

};

export const editPrebooking = async(prebook, prebookingId) => {
  const body = getPrebookBody(prebook);

  const param = requestService.getDefaultParams();
  try {
    const prebookData = await requestService.putWithJson(`${endpoints.editPrebooking}${encodeURIComponent(prebookingId)}/edit`, param, body, {apiType: 'platform'});
    showSuccess(i18n('prebook_successfully_edit'));
    return prebookData;
  } catch (e) {
    console.error(e);
    showError(i18n('error500'));
    throw e;
  }
};

export const acceptAutoAllocatedPrebooking = async (prebookingId) => {
  const params = requestService.getDefaultParams();

  try {
    const response = await requestService.putWithJson(`${endpoints.acceptPrebooking}${encodeURIComponent(prebookingId)}/accept`, params, null, {apiType: 'platform'});
    showSuccess(i18n('prebook_successfully_accepted'));
    return response;
  } catch (e) {
    console.log(e);
    showError(i18n('error500'));
    throw e;
  }
};

const getParamForCancelPrebooking = (params) => {
  if (params) {
    return Object.keys(params).reduce((memo, key) => {
      if (!memo && params[key]) {
        return `${key}=${encodeURIComponent(params[key])}`;
      }
      return params[key] ? `${memo}&${key}=${encodeURIComponent(params[key])}` : memo;
    }, '');
  }
  return '';
};

export const cancelPrebooking = async(prebookingId, params) => {
  const body = getParamForCancelPrebooking(params);
  try {
    return requestService.putWithJson(`${endpoints.cancelPrebooking}${encodeURIComponent(prebookingId)}`, body, params, {apiType: 'platform-v2'}).then((response) => {
      showSuccess(i18n('prebook_successfully_cancelled'));
      return response;
    });
  } catch (e) {
    console.error(e);

    if (e.response !== undefined && e.response.status === 400) {
      showError(i18n('error500'));
    } else {
      amplitude.getInstance().logEvent('Accept request failed', { 'HTTP status code': e.response ? String(e.response.status) : '' });
      showError(i18n('error500'));
    }

    throw e;
  }
};

export const confirmPrebookingBilling = async(prebookingId, userGroupId, invoiceRef, invoiceRecipientRef, customGroupValues, confirm = true) => {
  const data = {
    invoiceUserGroupId: userGroupId,
    invoiceRef: invoiceRef,
    invoiceRecipientRef: invoiceRecipientRef,
    customFields: customGroupValues,
    confirm
  };
  const param = requestService.getDefaultParams();
  const body = Object.keys(data).reduce((memo, field) => {
    // confirm must always be sent: confirm=false saves edits without
    // removing the row from the invoicing list (TUL-4891).
    if (data[field] || field === 'confirm') {
      memo[field] = data[field];
    }
    return memo;
  }, {});

  try {
    const prebookData = await requestService.putWithJson(`${endpoints.editPrebooking}${encodeURIComponent(prebookingId)}/edit`, param, body, {apiType: 'platform'});
    showSuccess(i18n(confirm ? 'prebook_successfully_updated' : 'prebook_successfully_edit'));
    return prebookData;
  } catch (e) {
    console.error(e);

    if (e.response !== undefined && e.response.status === 400) {
      showError(i18n('error500'));
    } else {
      amplitude.getInstance().logEvent('Accept request failed', { 'HTTP status code': e.response ? String(e.response.status) : '' });
      showError(i18n('error500'));
    }

    throw e;
  }
};

export const getFreeSlots = (body) => {
  try {
    const data = requestService.postWithJson(
      `${endpoints.getFreeSlots}`,
      null,
      body,
      { apiType: 'platform'}
    );
    return data;
  } catch (err) {
    console.error(err);
    return err;
  }
};

const getBodyForMultiPrebook = ({date, interpreterId, interpreterUserId, targetLanguageId, genericLanguageId, interpretedLanguageId, skill}) => {
  const defaultBody = {
    date: date ? new Date(date?.start).getTime() : null,
    duration: Math.max(date.duration || 0, 900),
    presence: 'remote',
    callType: 'data',
    appVariant: 'tulka',
  };
  let prebookings = [{
    interpreterUserId: interpreterUserId,
    interpreterId: interpreterId,
    targetLanguageId,
    languageId: genericLanguageId,
    skillIds: [skill]
  }];

  if (isInterpretationPrebookNeeded(interpretedLanguageId)) {
    prebookings.push({
      targetLanguageId,
      languageId: interpretedLanguageId,
    });
  }
  return {...defaultBody, prebookings };
};

export const createMultiPrebook = (prebookData) => {
  const body = getBodyForMultiPrebook(prebookData);
  try {
    const data = requestService.postWithJson(
      `${endpoints.createMulti}`,
      null,
      body,
      { apiType: 'platform'}
    );
    return data;
  } catch (err) {
    console.error(err);
    throw err;
  }
};

export const prebookingPrequery = async (prebookingData) => {
  try {
    const response = await requestService.postWithJson(
      endpoints.prebookingPrequery,
      null,
      { prebooking: prebookingData },
      { apiType: 'platform' }
    );
    return response;
  } catch (e) {
    console.error('Failed to run prebooking prequery:', e);
    throw e;
  }
};

export const getInterpreterCount = async (params) => {
  const queryParams = [];

  if (params.languageId) queryParams.push(`languageId=${params.languageId}`);
  if (params.targetLanguageId) queryParams.push(`targetLanguageId=${params.targetLanguageId}`);
  if (params.date) queryParams.push(`date=${params.date.getTime()}`);
  if (params.duration) queryParams.push(`duration=${params.duration * 60}`);
  if (params.gender) queryParams.push(`gender=${params.gender}`);
  if (params.skillIds && params.skillIds.length > 0) queryParams.push(`skillIds=${params.skillIds.join(',')}`);
  if (params.presence) queryParams.push(`presence=${params.presence}`);

  const queryString = queryParams.join('&');

  try {
    const response = await requestService.get(
      `${endpoints.getInterpreterCount}`,
      queryString,
      { apiType: 'platform-v2' }
    );
    return response.count || 0;
  } catch (e) {
    console.error('Failed to get interpreter count:', e);
    throw e;
  }
};

// Attachment API functions

// Files larger than this use the resumable multipart upload flow
// (see multipartUploadService.js); files at or below it keep the single-PUT path.
// MUST match the backend MULTIPART_THRESHOLD.
export const MULTIPART_THRESHOLD = 16 * 1024 * 1024;

const S3_UPLOAD_TIMEOUT_MS = 45000;

/**
 * Get a presigned URL for uploading an attachment to a prebooking
 * @param {string} prebookingId - The prebooking ID
 * @param {string} filename - Original filename
 * @param {number} fileSize - File size in bytes
 * @param {string} contentType - MIME type of the file
 * @param {number|null} unitScope - Estimated scope value
 * @param {string|null} unitType - Scope type (e.g. 'words', 'pages', 'seconds')
 * @param {boolean} replace - Whether to replace existing attachment
 * @returns {Promise<{uploadUrl: string, attachmentId: string, expiresIn: number}>}
 */
export const getAttachmentUploadUrl = async (prebookingId, filename, fileSize, contentType, unitScope, unitType, replace = false) => {
  try {
    const body = { filename, fileSize, contentType, replace };
    if (unitScope !== null && unitScope !== undefined) body.unitScope = unitScope;
    if (unitType !== null && unitType !== undefined) body.unitType = unitType;
    const response = await requestService.postWithJson(
      endpoints.attachmentPresignedUrl.replace('{prebookingId}', encodeURIComponent(prebookingId)),
      null,
      body,
      { apiType: 'platform' }
    );
    return response;
  } catch (e) {
    console.error('Failed to get attachment upload URL:', e);
    throw createAttachmentError(e, 'Failed to get upload URL');
  }
};

/**
 * Confirm that an attachment upload has completed
 * @param {string} prebookingId - The prebooking ID
 * @param {string} attachmentId - The attachment ID returned from getAttachmentUploadUrl
 * @returns {Promise<{success: boolean, attachment: object}>}
 */
export const confirmAttachmentUpload = async (prebookingId, attachmentId) => {
  try {
    const response = await requestService.postWithJson(
      endpoints.attachmentConfirm.replace('{prebookingId}', encodeURIComponent(prebookingId)),
      null,
      { attachmentId },
      { apiType: 'platform' }
    );
    return response;
  } catch (e) {
    console.error('Failed to confirm attachment upload:', e);
    throw createAttachmentError(e, 'Failed to confirm upload');
  }
};

/**
 * Get attachment metadata for a prebooking
 * @param {string} prebookingId - The prebooking ID
 * @returns {Promise<{id: string, filename: string, fileSize: number, contentType: string, status: string, canDownload: boolean}|null>}
 */
export const getAttachment = async (prebookingId) => {
  try {
    const response = await requestService.get(
      endpoints.attachment.replace('{prebookingId}', encodeURIComponent(prebookingId)),
      null,
      { apiType: 'platform' }
    );
    return response;
  } catch (e) {
    // Check for 404 using status code first, then fall back to message
    const status = getErrorStatus(e);
    if (status === 404 || e.message === 'Not Found') {
      return null;
    }
    console.error('Failed to get attachment:', e);
    throw createAttachmentError(e, 'Failed to get attachment');
  }
};

/**
 * Get a presigned URL for downloading/viewing an attachment
 * @param {string} prebookingId - The prebooking ID
 * @param {string} [attachmentId] - Optional specific attachment ID
 * @returns {Promise<{downloadUrl: string, expiresIn: number}>}
 */
export const getAttachmentDownloadUrl = async (prebookingId, attachmentId) => {
  try {
    const url = endpoints.attachmentDownload.replace('{prebookingId}', encodeURIComponent(prebookingId));
    const params = attachmentId ? `attachmentId=${encodeURIComponent(attachmentId)}` : null;
    const response = await requestService.get(
      url,
      params,
      { apiType: 'platform' }
    );
    return response;
  } catch (e) {
    console.error('Failed to get attachment download URL:', e);
    throw createAttachmentError(e, 'Failed to get download URL');
  }
};

/**
 * Delete an attachment from a prebooking
 * @param {string} prebookingId - The prebooking ID
 * @returns {Promise<boolean>}
 */
export const deleteAttachment = async (prebookingId) => {
  try {
    await requestService.delete(
      endpoints.attachment.replace('{prebookingId}', encodeURIComponent(prebookingId)),
      { apiType: 'platform' }
    );
    return true;
  } catch (e) {
    console.error('Failed to delete attachment:', e);
    throw createAttachmentError(e, 'Failed to delete attachment');
  }
};

/**
 * Upload a file directly to S3 using a presigned URL
 * @param {string} presignedUrl - The S3 presigned URL
 * @param {File} file - The file to upload
 * @param {Object} options - Optional upload settings
 * @param {function} options.onProgress - Progress callback (loaded, total)
 * @param {number} options.timeoutMs - Timeout in milliseconds
 * @returns {Promise<boolean>}
 */
export const uploadFileToS3 = async (presignedUrl, file, options = {}) => {
  const { onProgress, timeoutMs = S3_UPLOAD_TIMEOUT_MS } = options;

  if (!isTrustedS3Url(presignedUrl)) {
    throw createAttachmentError(new Error('Untrusted upload URL'), 'Failed to upload file');
  }

  const controller = typeof AbortController !== 'undefined' ? new AbortController() : null;
  const timeoutId = controller ? setTimeout(() => controller.abort(), timeoutMs) : null;

  try {
    // Use XMLHttpRequest when progress tracking is needed
    if (typeof onProgress === 'function' && typeof XMLHttpRequest !== 'undefined') {
      return await new Promise((resolve, reject) => {
        const xhr = new XMLHttpRequest();
        xhr.open('PUT', presignedUrl);
        xhr.responseType = 'text';
        xhr.timeout = timeoutMs;
        xhr.setRequestHeader('Content-Type', file.type || 'application/octet-stream');
        xhr.setRequestHeader('x-amz-server-side-encryption', 'AES256');
        xhr.upload.onprogress = (event) => {
          if (event.lengthComputable) {
            onProgress(event.loaded, event.total);
          }
        };
        xhr.onload = () => {
          if (xhr.status >= 200 && xhr.status < 300) {
            resolve(true);
          } else {
            reject(createAttachmentError(new Error(`S3 upload failed with status ${xhr.status}`), 'Failed to upload file'));
          }
        };
        xhr.onerror = () => reject(createAttachmentError(new Error('Network error during upload'), 'Failed to upload file'));
        xhr.ontimeout = () => reject(createAttachmentError(new Error('Upload timed out'), 'Failed to upload file'));
        xhr.send(file);
      });
    }

    // Note: Content-Length is automatically set by the browser for File objects
    // and is a forbidden header that cannot be set manually in fetch
    const response = await fetch(presignedUrl, {
      method: 'PUT',
      body: file,
      headers: {
        'Content-Type': file.type || 'application/octet-stream',
        'x-amz-server-side-encryption': 'AES256',
      },
      ...(controller ? { signal: controller.signal } : {})
    });

    if (!response.ok) {
      const error = new Error(`S3 upload failed with status ${response.status}`);
      error.status = response.status;
      throw error;
    }

    return true;
  } catch (e) {
    if (controller?.signal?.aborted) {
      const timeoutError = new Error('Upload timed out');
      timeoutError.status = 408;
      throw createAttachmentError(timeoutError, 'Failed to upload file');
    }
    console.error('Failed to upload file to S3:', e);
    throw createAttachmentError(e, 'Failed to upload file');
  } finally {
    if (timeoutId) {
      clearTimeout(timeoutId);
    }
  }
};
